import { Box, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/system';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import LineChart from 'src/components/HighChart/LineChart';

import WhiteBoxContent from 'src/components/common/WhiteBoxContent';
import Table from 'src/components/Table';
import { FORMAT_DATE_TIME, TABLE_PAGE_SIZE_12 } from 'src/utils/constants/config';
import { renderNumberOrDefault, renderTextOrDefault } from 'src/utils/helpers/stringHelpers';

import { isEmpty, omit } from 'lodash';
import { useTranslation } from 'react-i18next';
import FilterChart from 'src/features/TradeData/Common/FilterChart';
import { useQueryParams } from 'src/hooks/useQueryParam';
import {
  KeyFilterChartTradeDataUnitBy,
  KeyFilterChartTradeDataViewBy,
  TypeDataGranularLevel,
} from 'src/utils/constants/enum';
import {
  IGetTradeDataCompanyDetailTable,
  IGetTradeDataMarketAnalytics,
  ISearchGlobalTradeDataQueryParams,
} from 'src/utils/typings/dto/tradeData';

import useCreditFlow from 'src/hooks/useCreditFlow';
import { useTradeDataSortStore } from 'src/zustand/useTradeDataSortStore';

import { useParams } from 'react-router-dom';
import TooltipFormLabel from 'src/components/common/TooltipFormLabel';
import CompanyInfoNoData from 'src/features/CompanyDetail/Common/CompanyInfoNoData';
import { useQueryGetCTDMarketAnalyticsList } from 'src/services/queries/useQueryCompanyTradeData';
import { useQueryRuntimeConfig } from 'src/services/queries/useQueryRuntimeConfig';
import { LIST_ACTIONS, LIST_FEATURES } from 'src/utils/constants/featuresPoint';
import { getArrayDefault } from 'src/utils/helpers/arrayHelpers';
import { formatValueUSD } from 'src/utils/helpers/utilities';

const MarketAnalytics = () => {
  const {
    palette: { text },
  } = useTheme();
  const {
    data: { data: runtimeConfig },
  } = useQueryRuntimeConfig();

  const { id: companyId } = useParams();

  const { t } = useTranslation();
  const queryParams = useQueryParams() as ISearchGlobalTradeDataQueryParams;

  const { setSort: setTableSort, marketAnalyticsTableSortParams } = useTradeDataSortStore();
  const { setSort: setPopupTableSort, marketAnalyticsPopupTableSortParams } = useTradeDataSortStore();

  const [marketAnalyticsTablePaging, setMarketAnalyticsTablePagingModel] = useState({
    page: 0,
    page_size: TABLE_PAGE_SIZE_12,
  });

  const [marketAnalyticsCompanyTablePaging, setMarketAnalyticsCompanyTablePaging] = useState({
    page: 0,
    page_size: 10,
  });

  const [unitFilter, setUnitFilter] = useState<KeyFilterChartTradeDataUnitBy>(KeyFilterChartTradeDataUnitBy.bol);
  const [viewByFilter, setViewByFilter] = useState<KeyFilterChartTradeDataViewBy>(
    KeyFilterChartTradeDataViewBy.monthly,
  );
  const [selectedRangeTime, setSelectedRangeTime] = useState({
    fromTime:
      queryParams.start_date ||
      dayjs(runtimeConfig.DEFAULT_TRADE_DATA_FROM, 'YYYY-MM-DD').format(FORMAT_DATE_TIME.yyyy_mm_dd),
    toTime:
      queryParams.end_date ||
      dayjs(runtimeConfig.DEFAULT_TRADE_DATA_TO, 'YYYY-MM-DD').format(FORMAT_DATE_TIME.yyyy_mm_dd),
  } as {
    fromTime: string;
    toTime: string;
  });

  const granularLevel =
    viewByFilter === KeyFilterChartTradeDataViewBy.annual
      ? TypeDataGranularLevel.yearly
      : TypeDataGranularLevel.monthly;

  // useEffect(() => {
  //   setTableSort('marketAnalyticsTableSortParams', {
  //     sortBy: granularLevel === TypeDataGranularLevel.monthly ? 'month' : 'year',
  //     sort: 'desc'
  //   })
  // }, [granularLevel]);

  const unitFieldMapping = {
    bol: {
      field: 'number_of_bol',
      label: 'Number of B/L',
    },
    weight: {
      field: 'weight',
      label: 'Weight',
    },
    quantity: { field: 'quantity', label: 'Quantity' },
    value_usd: { field: 'value', label: 'USD' },
  };

  const marketAnalyticsCommonRequestPayload: IGetTradeDataMarketAnalytics = useMemo(() => {
    return {
      ...omit(queryParams, [
        //
      ]),
      start_date: selectedRangeTime.fromTime,
      end_date: selectedRangeTime.toTime,
    };
  }, [queryParams, selectedRangeTime]);

  const marketAnalyticsTableRequestPayload: IGetTradeDataMarketAnalytics = useMemo(() => {
    return {
      ...queryParams,
      start_date: selectedRangeTime.fromTime,
      end_date: selectedRangeTime.toTime,
      company_id: companyId || '',
      offset: marketAnalyticsTablePaging.page,
      limit: marketAnalyticsTablePaging.page_size,
      sort_by: marketAnalyticsTableSortParams?.sortBy,
      order: marketAnalyticsTableSortParams?.sort,
    };
  }, [queryParams, marketAnalyticsTablePaging, selectedRangeTime, marketAnalyticsTableSortParams]);

  const chartLabelByUnitFilter = unitFieldMapping[unitFilter].label;
  const chartFieldNameByUnitFilter = unitFieldMapping[unitFilter].field;

  const [marketAnalyticsCompanyRequestPayload, setMarketAnalyticsCompanyRequestPayload] =
    useState<IGetTradeDataCompanyDetailTable>();
  const [acceptCreditPayment, setAcceptCreditPayment] = useState(false);

  // const marketAnalyticsChartResp = useQueryGetCTDMarketAnalyticsChart(
  //   companyId || '',
  //   marketAnalyticsCommonRequestPayload,
  //   granularLevel,
  // );

  const marketAnalyticsTableResp = useQueryGetCTDMarketAnalyticsList(
    companyId || '',
    marketAnalyticsTableRequestPayload,
    granularLevel,
  );

  // const marketAnalyticsDataChart = marketAnalyticsChartResp.data?.data;
  const marketAnalyticsDataTable = marketAnalyticsTableResp.dataMapping;

  // const transactionsPaymentStatusResp = useQueryTransactionsPaymentStatus(marketAnalyticsCommonRequestPayload);

  const [inputDateFormat, outputDateFormat] = useMemo(() => {
    return granularLevel === TypeDataGranularLevel.monthly
      ? [FORMAT_DATE_TIME.yyyy_mm, FORMAT_DATE_TIME.MONTH_YEAR]
      : [FORMAT_DATE_TIME.FULL_YEAR, FORMAT_DATE_TIME.FULL_YEAR];
  }, [granularLevel]);

  const [shouldShowBillableDataLoading] = useCreditFlow({
    response: marketAnalyticsTableResp.data,
    featureInfo: {
      featureId: LIST_FEATURES.TRADE_DATA_BILL_OF_LADING,
      action: LIST_ACTIONS.VIEW,
    },
    // paymentStatus: transactionsPaymentStatusResp.data?.data?.bol,
    // isCollectingData: transactionsPaymentStatusResp.isFetching,
    acceptCreditPayment,
    onSetAcceptCreditPayment: (value) => setAcceptCreditPayment(value),
  });

  const lineChartYData = useMemo(() => {
    return marketAnalyticsDataTable.map((item: IMarketAnalytics) => [
      item?.[chartFieldNameByUnitFilter as keyof IMarketAnalytics],
    ]);
  }, [marketAnalyticsDataTable, chartFieldNameByUnitFilter]);

  const lineChartXData = useMemo(() => {
    return marketAnalyticsDataTable.map((item: IMarketAnalytics) =>
      dayjs(item?.date, inputDateFormat).format(outputDateFormat),
    );
  }, [marketAnalyticsDataTable]);

  useEffect(() => {
    if (!marketAnalyticsCompanyRequestPayload) return;
    setMarketAnalyticsCompanyRequestPayload({
      ...marketAnalyticsCompanyRequestPayload,
      offset: marketAnalyticsCompanyTablePaging.page,
      limit: marketAnalyticsCompanyTablePaging.page_size,
      sort_by: marketAnalyticsPopupTableSortParams?.sortBy,
      order: marketAnalyticsPopupTableSortParams?.sort,
    });
  }, [marketAnalyticsCompanyTablePaging, marketAnalyticsCompanyRequestPayload, marketAnalyticsPopupTableSortParams]);

  const configMarketAnalyticsTableColumns: ConfigCell<IMarketAnalytics>[] = [
    {
      disablePadding: false,
      id: 'month',
      label:
        granularLevel === TypeDataGranularLevel.monthly
          ? t('market_analytics.table.column_name.month')
          : t('market_analytics.table.column_name.year'),
      cell: (data: IMarketAnalytics) => (
        <Typography variant="body2" color="text.secondary">
          {dayjs(data.date, inputDateFormat).format(outputDateFormat)}
        </Typography>
      ),
    },
    {
      disablePadding: false,
      id: 'bol',
      label: t('market_analytics.table.column_name.no_of_bl'),
      cell: (data: IMarketAnalytics) => (
        <Typography variant="body2" color="text.secondary">
          {renderNumberOrDefault(data.number_of_bol)}
        </Typography>
      ),
    },
    {
      disablePadding: false,
      id: 'weight',
      label: t('market_analytics.table.column_name.weight'),
      cell: (data: IMarketAnalytics) => (
        <Typography variant="body2" color="text.secondary">
          {renderNumberOrDefault(data.weight)}
        </Typography>
      ),
    },
    {
      disablePadding: false,
      id: 'quantity',
      label: t('market_analytics.table.column_name.teu'),
      cell: (data: IMarketAnalytics) => (
        <Typography variant="body2" color={'text.secondary'}>
          {renderTextOrDefault(data.quantity)}
        </Typography>
      ),
    },
    {
      disablePadding: false,
      id: 'value_usd',
      label: (
        <Stack direction={'row'} gap={1} alignItems={'center'}>
          <Box>{t('market_analytics.table.column_name.value')}</Box>
          <TooltipFormLabel title={t('trade_data.value_usd.disclaimer.tooltips')} />
        </Stack>
      ),
      cell: (data: IMarketAnalytics) => (
        <Typography variant="body2" color={'text.secondary'}>
          {formatValueUSD(data.value_usd)}
        </Typography>
      ),
    },
  ];

  const isDataReturnedEmpty =
    !marketAnalyticsTableResp.isFetching && isEmpty(getArrayDefault(marketAnalyticsDataTable));

  return (
    <Stack direction={'column'} gap={3}>
      <WhiteBoxContent display={'flex'} flexDirection={'column'} gap={3}>
        <FilterChart
          selectedUnitBy={unitFilter}
          selectedRangeTime={selectedRangeTime}
          selectedViewBy={viewByFilter}
          onSelectTimeRange={({ fromTime, toTime }) => {
            setSelectedRangeTime({
              fromTime: fromTime,
              toTime: toTime,
            });
          }}
          title={t('market_analytics.graph.ctd_market_trade_data.title')}
          onSelectUnitBy={(unit) => {
            setUnitFilter(unit as any);
          }}
          onSelectViewBy={(unit) => {
            setViewByFilter(unit as any);
            setTableSort('marketAnalyticsTableSortParams', {
              sortBy: unit === KeyFilterChartTradeDataViewBy.monthly ? 'month' : 'year',
              sort: 'asc',
            });
          }}
        />

        {!isDataReturnedEmpty ? (
          <LineChart
            loading={shouldShowBillableDataLoading || marketAnalyticsTableResp.isFetching}
            dataPropertyKeys={['y', 'custom.number_of_suppliers', 'custom.number_of_buyers']}
            tooltipFormatter={function (this) {
              return `<b>${dayjs(this.x, outputDateFormat).format(granularLevel === TypeDataGranularLevel.monthly ? FORMAT_DATE_TIME.LONG_MONTH_YEAR : FORMAT_DATE_TIME.FULL_YEAR)}</b><br>
            <span style="color: ${text.secondary}">${chartLabelByUnitFilter}: <b style="color: white">${renderNumberOrDefault(this.y)}</b></span><br>`;
            }}
            data={lineChartYData}
            categories={lineChartXData}
          />
        ) : (
          <CompanyInfoNoData
            title={t('trade_data.common.notice_no_data.title')}
            content={<>{t('trade_data.common.notice_no_data.sub_title')}</>}
          />
        )}
      </WhiteBoxContent>
      {!isDataReturnedEmpty && (
        <WhiteBoxContent>
          <Typography textAlign={'left'} variant="h5">
            {t('market_analytics.chart.ctd_market_trade_data.title')}
          </Typography>
          <Box mt={3} />
          <Table
            maxRow={50}
            isPagination={false}
            handleSort={(data) => {
              setTableSort('marketAnalyticsTableSortParams', data);
            }}
            onChangePage={(value) => setMarketAnalyticsTablePagingModel({ ...marketAnalyticsTablePaging, page: value })}
            onChangePageSize={(value) => setMarketAnalyticsTablePagingModel({ page: 0, page_size: value })}
            loading={shouldShowBillableDataLoading || marketAnalyticsTableResp.isFetching}
            page={marketAnalyticsTablePaging.page}
            pageSize={marketAnalyticsTablePaging.page_size}
            totalRecords={marketAnalyticsTableResp.data?.total_records}
            dataKey={'date'}
            data={marketAnalyticsDataTable}
            configColumn={configMarketAnalyticsTableColumns}
          />
        </WhiteBoxContent>
      )}
    </Stack>
  );
};

export default MarketAnalytics;
